// src/app/pages/auditcentre/[id].tsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AuditAccordion } from "@/components/audit-centre/AuditAccordion";
import { AuditLinkedFindingsTable } from "@/components/audit-centre/AuditLinkedFindingsTable";
import { AuditLinkedDocuments } from "@/components/audit-centre/AuditLinkedDocuments";
import { AuditService } from "./auditService";
import { useActiveTenancy } from "./useActiveTenancy";
import type { Audit, Incident, Finding, CorrectiveAction } from "./index";
import { FindingTable } from "@/components/audit-centre/FindingTable";
import { routePathByName } from "@/app/routes";
import { ArrowLeft, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTenancy } from "@/contexts/TenancyContext";
import { AuditEditDialog } from "@/components/audit-centre/AuditEditDialog";
import { MCMMappingsCard } from "@/components/mcm/MCMMappingsCard";


export default function AuditDetailPage() {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const activeTenancy = useActiveTenancy();
  const { userIdToName } = useTenancy();
  const [audit, setAudit] = useState<Audit | null>(null);
  const [allIncidents, setAllIncidents] = useState<(Incident & { auditId?: string })[]>([]);
  const [allFindings, setAllFindings] = useState<(Finding & { auditId?: string })[]>([]);
  const [allCorrectiveActions, setAllCorrectiveActions] = useState<CorrectiveAction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Initialize audit service
  const auditService = React.useMemo(() => {
    return activeTenancy ? new AuditService({ activeTenancy }) : null;
  }, [activeTenancy]);

  // Load audit and related data - moved outside useEffect so it can be called from handlers
  const loadData = React.useCallback(async () => {
    if (!auditService || !id) return;

    try {
      setIsLoading(true);
      setError(null);

      // Load audit data and related collections in parallel
      const [
        auditData,
        auditFindings,
        standaloneFindings,
        incidentsData,
        correctiveActions
      ] = await Promise.all([
        auditService.getAudit(id),
        auditService.listFindings(id),
        auditService.listStandaloneFindings(),
        auditService.listIncidents(),
        auditService.listStandaloneCorrectiveActions()
      ]);

      if (auditData) {
        setAudit(auditData);

        // Process incidents - standalone and those linked to this audit
        const auditIncidents = incidentsData.filter(i => i.auditId === id);
        const otherIncidents = incidentsData.filter(i => i.auditId !== id);

        setAllIncidents([...auditIncidents, ...otherIncidents]);

        // Process findings - both from audit and standalone
        const auditFindingsWithId = auditFindings.map(f => ({
          ...f,
          auditId: id,
          // Ensure correctiveActions is always an array
          correctiveActions: Array.isArray(f.correctiveActions) ? f.correctiveActions : []
        }));

        // Filter out findings that belong to this audit to avoid duplicates
        // since listStandaloneFindings() returns ALL findings (standalone + audit-linked)
        const auditFindingIds = new Set(auditFindings.map(f => f.id));
        const standaloneFindingsWithId = standaloneFindings
          .filter(f => !auditFindingIds.has(f.id))
          .map(f => ({
            ...f,
            // Ensure correctiveActions is always an array
            correctiveActions: Array.isArray(f.correctiveActions) ? f.correctiveActions : []
          }));

          // DED-1678: Deduplicate findings since listStandaloneFindings now returns all findings
          // including audit findings. Use a Map to keep only unique findings by ID.
          const findingsMap = new Map<string, Finding & { auditId?: string }>();
          
          // Add audit findings first (they have priority)
          auditFindingsWithId.forEach(f => findingsMap.set(f.id, f));
          
          // Add standalone findings only if not already present
          standaloneFindingsWithId.forEach(f => {
            if (!findingsMap.has(f.id)) {
              findingsMap.set(f.id, f);
            }
          });

          setAllFindings(Array.from(findingsMap.values()));

        // Process corrective actions
        setAllCorrectiveActions(correctiveActions || []);
      } else {
        setError("Audit not found");
      }
    } catch (err) {
      console.error("Error loading audit data:", err);
      setError("Failed to load audit data. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  }, [auditService, id]);

  // Load data on mount and when dependencies change
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle linking/unlinking incidents
  const handleLinkIncident = async (incidentId: string, auditId: string): Promise<boolean> => {
    if (!auditService) return false;
    try {
      await auditService.linkIncidentToAudit(incidentId, auditId);
      setAllIncidents(prev =>
        prev.map(i => i.id === incidentId ? { ...i, auditId } : i)
      );
      return true;
    } catch (err) {
      console.error("Failed to link incident:", err);
      setError("Failed to link incident. Please try again.");
      return false;
    }
  };

  const handleUnlinkIncident = async (incidentId: string): Promise<boolean> => {
    if (!auditService) return false;
    try {
      await auditService.unlinkIncidentFromAudit(incidentId);
      setAllIncidents(prev =>
        prev.map(i => i.id === incidentId ? { ...i, auditId: undefined } : i)
      );
      return true;
    } catch (err) {
      console.error("Failed to unlink incident:", err);
      setError("Failed to unlink incident. Please try again.");
      return false;
    }
  };

  const handleCreateIncident = async (incident: {
    summary: string;
    owner: string;
    status: "Open" | "Closed";
    auditId?: string;
    dateOfIncident?: Date | string;
    impact?: string;
    dateClosed?: Date | string;
    closeComment?: string;
    controls?: string[];
    supportingDocuments?: string[];
  }): Promise<boolean> => {
    console.log('handleCreateIncident called with:', incident);
    if (!auditService) {
      console.error('Audit service not available');
      return false;
    }

    try {
      const newIncident = await auditService.createIncident({
        ...incident,
        auditId: audit?.id,
        createdAt: new Date(),
        updatedAt: new Date()
      });

      console.log('Incident created with ID:', newIncident.id);

      // Update the incidents state with the new incident
      setAllIncidents(prev => [...prev, newIncident]);

      console.log('Incident created and state updated successfully');
      return true;
    } catch (err) {
      console.error("Failed to create incident:", err);
      setError(`Failed to create incident: ${err.message}`);
      return false;
    }
  };

  const handleUpdateIncident = async (incidentId: string, updates: Partial<Incident>): Promise<boolean> => {
    if (!auditService) return false;

    try {
      // Update the incident in Firestore
      await auditService.updateIncident(incidentId, {
        ...updates,
        updatedAt: new Date()
      });

      // Update the local state
      setAllIncidents(prev =>
        prev.map(incident =>
          incident.id === incidentId
            ? { ...incident, ...updates, updatedAt: new Date() }
            : incident
        )
      );

      return true;
    } catch (err) {
      console.error("Failed to update incident:", err);
      setError(`Failed to update incident: ${err.message}`);
      return false;
    }
  };

  // Handle linking/unlinking findings
  const handleLinkFinding = async (findingId: string, auditId: string): Promise<boolean> => {
    if (!auditService) return false;
    try {
      await auditService.linkStandaloneFindingToAudit(findingId, auditId);
      setAllFindings(prev =>
        prev.map(f => f.id === findingId ? { ...f, auditId } : f)
      );
      return true;
    } catch (err) {
      console.error("Failed to link finding:", err);
      setError("Failed to link finding. Please try again.");
      return false;
    }
  };

  const handleUnlinkFinding = async (findingId: string): Promise<boolean> => {
    if (!auditService || !audit) return false;
    try {
      // Find the finding in our local state
      const finding = allFindings.find(f => f.id === findingId);

      if (!finding) {
        console.error(`Finding ${findingId} not found`);
        return false;
      }

      // Check if this finding is actually linked to the current audit
      if (finding.auditId !== audit.id) {
        console.log(`Finding ${findingId} is not linked to this audit, nothing to do`);
        return false;
      }

      console.log(`Unlinking finding ${findingId} from audit ${audit.id} (origin: ${finding.origin})`);

      // Handle based on the finding's origin
      if (finding.origin === 'audit') {
        // Move audit-specific finding to standalone collection
        const newFindingId = await auditService.moveFindingToStandalone(audit.id, findingId);
        console.log(`Moved finding ${findingId} to standalone as ${newFindingId}`);
      } else {
        // Just unlink standalone finding from audit
        await auditService.unlinkStandaloneFindingFromAudit(findingId);
        console.log(`Unlinked standalone finding ${findingId} from audit`);
      }

      // Update the local state to reflect the change
      setAllFindings(prev =>
        prev.map(f => f.id === findingId ? { ...f, auditId: undefined } : f)
      );

      return true;
    } catch (err) {
      console.error("Failed to unlink finding:", err);
      setError(`Failed to unlink finding: ${err.message}`);
      return false;
    }
  };

  const handleUpdateFinding = async (findingId: string, updates: Partial<Finding>): Promise<boolean> => {
    if (!auditService || !audit) return false;

    try {
      // Find the finding in our local state
      const finding = allFindings.find(f => f.id === findingId);
      if (!finding) {
        throw new Error('Finding not found');
      }

      // Preserve the original origin in updates
      const updateData = {
        ...updates,
        origin: finding.origin, // Always preserve the original origin
        updatedAt: new Date()
      };

      // Handle based on the finding's origin and current audit association
      if (finding.origin === 'audit') {
        // For audit-native findings, always update in the audit
        await auditService.updateFinding(audit.id, findingId, updateData);
      } else if (finding.auditId) {
        // For standalone findings linked to an audit, update in the standalone collection
        await auditService.updateStandaloneFinding(findingId, updateData);
      } else {
        // For standalone findings not linked to an audit
        await auditService.updateStandaloneFinding(findingId, updateData);
      }

      // Refresh the data to ensure consistency
      await loadData();
      return true;
    } catch (err) {
      console.error("Failed to update finding:", err);
      setError(`Failed to update finding: ${err.message}`);
      return false;
    }
  };

  const handleCreateFinding = async (finding: {
    summary: string;
    owner: string;
    status: "Open" | "Closed";
    type: "Minor NC" | "Major NC" | "OFI";
    auditId?: string;
    origin?: 'audit' | 'standalone';
  }) => {
    if (!auditService || !audit) return false;

    try {
      await auditService.createFinding(audit.id, {
        ...finding,
        origin: 'audit',
        correctiveActions: [],
        createdAt: new Date(),
        updatedAt: new Date()
      });

      // Refresh the data
      await loadData();
      return true;
    } catch (err) {
      console.error("Failed to create finding:", err);
      setError(`Failed to create finding: ${err.message}`);
      return false;
    }
  };

  // Handle corrective action operations
  const handleLinkCorrectiveAction = async (actionId: string, findingId: string): Promise<boolean> => {
    if (!auditService) return false;
    try {
      await auditService.linkCorrectiveActionToFinding(actionId, findingId);
      // Refresh the data to ensure consistency
      await loadData();
      return true;
    } catch (err) {
      console.error("Failed to link corrective action:", err);
      setError(`Failed to link corrective action: ${err.message}`);
      return false;
    }
  };

  const handleUnlinkCorrectiveAction = async (actionId: string): Promise<boolean> => {
    if (!auditService) return false;
    try {
      console.log("Unlinking corrective action:", actionId);
      await auditService.unlinkCorrectiveAction(actionId);
      // Optimistically clear the finding link so UI updates immediately
      setAllCorrectiveActions(prev => prev.map(a => a.id === actionId ? { ...a, findingId: undefined } : a));
      // Reconcile with backend after propagation
      setTimeout(() => { loadData().catch(() => { }) }, 750);
      return true;
    } catch (err) {
      console.error("Failed to unlink corrective action:", err);
      setError(`Failed to unlink corrective action: ${err.message}`);
      return false;
    }
  };

  const handleUnlinkFromAudit = async (actionId: string): Promise<boolean> => {
    if (!auditService) return false;
    try {
      console.log("Unlinking corrective action from audit:", actionId);
      await auditService.unlinkCorrectiveActionFromAudit(actionId);
      // Optimistically clear the audit link so UI updates immediately
      setAllCorrectiveActions(prev => prev.map(a => a.id === actionId ? { ...a, auditId: undefined } : a));
      // Reconcile with backend after propagation
      setTimeout(() => { loadData().catch(() => { }) }, 750);
      return true;
    } catch (err) {
      console.error("Failed to unlink corrective action from audit:", err);
      setError(`Failed to unlink corrective action from audit: ${err.message}`);
      return false;
    }
  };

  const handleCreateCorrectiveAction = async (
    action: Omit<CorrectiveAction, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<CorrectiveAction> => {
    if (!auditService || !audit) throw new Error('Audit service or audit not available');

    try {
      const newAction = await auditService.createStandaloneCorrectiveAction({
        ...action,
        auditId: audit.id,
        status: 'Open' as const,
        owner: action.owner || 'Unassigned',
        summary: action.summary || 'No summary provided',
      });

      // Update local state immediately
      setAllCorrectiveActions(prev => [...prev, newAction]);

      return newAction;
    } catch (err) {
      console.error("Failed to create corrective action:", err);
      throw new Error(`Failed to create corrective action: ${err.message}`);
    }
  };

  const handleUpdateCorrectiveAction = async (
    actionId: string,
    updates: Partial<Omit<CorrectiveAction, 'id' | 'createdAt' | 'updatedAt'>>
  ): Promise<boolean> => {
    if (!auditService) return false;

    try {
      // First, update the corrective action in Firestore and wait for it to complete
      await auditService.updateStandaloneCorrectiveAction(actionId, updates);

      // Get the updated action from Firestore to ensure we have the latest data
      const updatedAction = await auditService.getCorrectiveAction(actionId);

      if (!updatedAction) {
        throw new Error('Failed to retrieve updated corrective action');
      }

      const now = new Date();

      // Update the corrective action in allCorrectiveActions
      setAllCorrectiveActions(prev =>
        prev.map(action =>
          action.id === actionId
            ? {
              ...action,
              ...updatedAction,
              updatedAt: now,
              // Ensure other required fields are preserved
              createdAt: action.createdAt || now,
              status: updatedAction.status || 'Open',
              summary: updatedAction.summary || '',
              owner: updatedAction.owner || 'Unassigned',
              findingId: updatedAction.findingId
            }
            : action
        )
      );

      // If the corrective action is linked to a finding, update the finding's correctiveActions array
      if (updatedAction.findingId) {
        setAllFindings(prevFindings =>
          prevFindings.map(finding => {
            if (finding.id === updatedAction.findingId) {
              // Find the index of the corrective action to update
              const existingActionIndex = finding.correctiveActions?.findIndex(ca => ca.id === actionId) ?? -1;

              // If the action exists in the finding, update it; otherwise, add it
              const updatedCorrectiveActions = [...(finding.correctiveActions || [])];
              if (existingActionIndex >= 0) {
                updatedCorrectiveActions[existingActionIndex] = {
                  ...updatedCorrectiveActions[existingActionIndex],
                  ...updatedAction,
                  updatedAt: now
                };
              } else {
                updatedCorrectiveActions.push({
                  ...updatedAction,
                  id: actionId,
                  updatedAt: now,
                  createdAt: now
                });
              }

              return {
                ...finding,
                correctiveActions: updatedCorrectiveActions
              };
            }
            return finding;
          })
        );
      }

      return true;
    } catch (err) {
      console.error("Failed to update corrective action:", err);
      setError(`Failed to update corrective action: ${err.message}`);
      return false;
    }
  };

  if (isLoading) {
    return <div className="p-8">Loading audit...</div>;
  }

  if (error || !audit) {
    return <div className="p-8 text-red-500">{error || "Audit not found"}</div>;
  }

  // Filter incidents and findings for this audit
  const auditIncidents = allIncidents.filter(i => i.auditId === audit.id);
  const auditFindings = allFindings.filter(f => f.auditId === audit.id);

  // Get IDs of all findings linked to this audit
  const auditFindingIds = new Set(auditFindings.map(f => f.id));

  // Filter corrective actions to only show those linked to this audit or its findings
  const filteredCorrectiveActions = allCorrectiveActions.filter(action => {
    // Include if directly linked to this audit
    if (action.auditId === audit.id) return true;

    // Include if linked to a finding that's linked to this audit
    if (action.findingId && auditFindingIds.has(action.findingId)) return true;

    return false;
  });

  // Enrich corrective actions with their finding data
  const enrichedCorrectiveActions = filteredCorrectiveActions.map(action => ({
    ...action,
    finding: allFindings.find(f => f.id === action.findingId)
  }));

  return (
    <div className="p-8 min-h-screen">
      <div className="flex flex-row gap-2 items-center">
        {/* Back to Audit Centre button - just a back icon in a button */}
        {/* DED-1141: Fixed back button to use browser history instead of hardcoded route */}
        <Button variant="outline" className="bg-card" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <h1 className="text-2xl font-bold mb-6">Audit Detail</h1>
        {/* DED-1141: Improved visibility of Edit button with primary variant and icon */}
        <Button
          variant="default"
          className="ml-auto gap-2"
          onClick={() => setEditDialogOpen(true)}
        >
          <Pencil className="w-4 h-4" />
          Edit Audit
        </Button>
      </div>
      <Card className="glass-card p-6 mb-6">
        <div className="flex flex-col gap-4">
          <div><b>Type:</b> {audit.type}</div>
          <div><b>Scope:</b> {audit.scope}</div>
          <div><b>Owners:</b> {Array.isArray(audit.owners)
            ? audit.owners.map(owner => userIdToName[owner] || owner).join(", ")
            : userIdToName[audit.owners] || audit.owners}</div>
          <div><b>Auditor:</b> {audit.auditor}</div>

          {/* Controls Section */}
          {audit.controls && audit.controls.length > 0 && (
            <div className="grid grid-cols-1 items-center gap-4 mt-2">
              <Accordion type="single" collapsible>
                <AccordionItem value="controls" className="border rounded-lg shadow-sm bg-white dark:bg-background">
                  <AccordionTrigger className="px-4 py-3 text-base font-medium flex items-center gap-2">
                    <span className="flex-1 text-left">Controls / Clauses in Scope</span>
                    <span className="ml-auto text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                      {audit.controls.length} selected
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <ScrollArea className="max-h-[300px] overflow-auto">
                      <div className="border border-gray-200 rounded-md p-3">
                        <div className="flex flex-wrap gap-2">
                          {audit.controls.map(control => (
                            <div key={control} className="px-3 py-1.5 bg-primary/10 text-primary rounded-md text-sm font-medium">
                              {control}
                            </div>
                          ))}
                        </div>
                      </div>
                    </ScrollArea>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          )}
        </div>
      </Card>
      {/* Audit Edit Dialog */}
      <AuditEditDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        audit={audit}
        onSave={async (updates) => {
          if (!auditService || !audit?.id) return;
          setIsLoading(true);
          try {
            const updated = await auditService.updateAudit(audit.id, updates);
            // setAudit(updated);
            await loadData();
            setEditDialogOpen(false);
          } catch (err) {
            setError("Failed to update audit");
          } finally {
            setIsLoading(false);
          }
        }}
        availableOwners={Object.keys(userIdToName)}
      />

      {/* DED-1141: Updated to include corrective actions as expandable rows under each finding */}
      <AuditLinkedFindingsTable
        findings={auditFindings}
        audit={audit}
        isLoading={isLoading}
        onLink={handleLinkFinding}
        onUnlink={handleUnlinkFinding}
        onUpdate={handleUpdateFinding}
        onCreate={handleCreateFinding}
        auditService={auditService}
        correctiveActions={filteredCorrectiveActions}
        onCreateCorrectiveAction={handleCreateCorrectiveAction}
        onUpdateCorrectiveAction={handleUpdateCorrectiveAction}
        onUnlinkCorrectiveAction={handleUnlinkCorrectiveAction}
        inheritedControls={audit?.controls || []}
      />

      <AuditLinkedDocuments
        auditId={audit.id}
        auditScope={audit.scope}
        defaultDocumentType="Audit"
        linkedDocuments={(audit as any).linkedDocuments || []}
        isReadOnly={false}
        onDocumentsChange={async (docIds) => {
          if (!auditService || !audit?.id) return;
          try {
            await auditService.updateAudit(audit.id, { linkedDocuments: docIds } as any);
            setAudit(prev => prev ? ({ ...prev, linkedDocuments: docIds } as any) : prev);
          } catch (err) {
            setError("Failed to update linked documents");
          }
        }}
      />

      {/* DED-1141: Removed LinkedCorrectiveActionsTable - corrective actions are now shown under each finding */}

      {/* DED-1141: Removed linked incidents section from Audit Detail page */}

      <MCMMappingsCard
        tenancyId={activeTenancy?.id}
        isReadOnly={false}
        controlIds={(audit.controls || []).map(String)}
        defaultEntityLink={{
          id: `internal:audit:${String(audit.id)}`,
          ref: String(audit.id), 
          title: audit.scope || audit.type || 'Audit',
          domain: 'Audit',
        }}
        onEnsureControlLinked={(controlId) => {
          setAudit(prev => prev ? {
            ...prev,
            controls: Array.from(new Set([...(prev.controls || []), String(controlId)]))
          } : prev);
        }}
        collapsible
        className="w-full!"
        maxWidth="none"
      />
    </div>
  );
}